import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Star, Send, CheckCircle, MapPin, Loader2,
    MessageSquare, Quote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

function StarRating({ rating, onChange, readonly = false, size = 'md' }) {
    const sizes = { sm: 'h-4 w-4', md: 'h-6 w-6', lg: 'h-8 w-8' };
    return (
        <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    disabled={readonly}
                    onClick={() => !readonly && onChange?.(n)}
                    className={readonly ? 'cursor-default' : 'hover:scale-110 transition'}
                    data-testid={readonly ? undefined : `star-${n}`}
                >
                    <Star
                        className={`${sizes[size]} ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`}
                    />
                </button>
            ))}
        </div>
    );
}

function formatRelativeDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function SiteReviewsPage() {
    const { user } = useAuth();
    const [data, setData] = useState({ reviews: [], stats: { average_rating: 0, total_reviews: 0, rating_distribution: {} } });
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ rating: 5, title: '', comment: '', name: '', city: '', topic: 'service' });

    const load = async () => {
        setLoading(true);
        try {
            const d = await apiRequest('/site-reviews?limit=50');
            setData(d);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title || form.title.length < 3) { toast.error('Donnez un titre'); return; }
        if (!form.comment || form.comment.length < 10) { toast.error('Votre commentaire doit faire au moins 10 caractères'); return; }
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (user) { delete payload.name; }
            const res = await apiRequest('/site-reviews', { method: 'POST', body: JSON.stringify(payload) });
            toast.success(res.message || 'Merci pour votre avis !');
            setShowForm(false);
            setForm({ rating: 5, title: '', comment: '', name: '', city: '', topic: 'service' });
            load();
        } catch (e) {
            toast.error(e.message || "Erreur lors de l'envoi");
        } finally {
            setSubmitting(false);
        }
    };

    const stats = data.stats;
    const dist = stats.rating_distribution || {};
    const maxCount = Math.max(...Object.values(dist), 1);

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="site-reviews-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
                {/* Hero */}
                <div className="text-center">
                    <Badge className="mb-3 bg-amber-500/10 text-amber-600 border-amber-500/30">
                        <Star className="h-3 w-3 fill-amber-500 text-amber-500 mr-1" />
                        Avis vérifiés de nos clients
                    </Badge>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Ils nous ont fait confiance</h1>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        Découvrez ce que pensent nos clients du Var et de la Côte d&apos;Azur d&apos;O.C.A Piscine
                    </p>
                </div>

                {/* Stats hero */}
                <Card className="glass rounded-3xl overflow-hidden">
                    <CardContent className="p-6 sm:p-8">
                        <div className="grid md:grid-cols-3 gap-6 items-center">
                            {/* Average */}
                            <div className="text-center md:text-left">
                                <div className="flex items-center gap-3 justify-center md:justify-start">
                                    <span className="text-6xl font-bold font-heading">{stats.average_rating.toFixed(1)}</span>
                                    <div>
                                        <StarRating rating={Math.round(stats.average_rating)} readonly size="md" />
                                        <p className="text-sm text-muted-foreground mt-1">{stats.total_reviews} avis</p>
                                    </div>
                                </div>
                            </div>
                            {/* Distribution */}
                            <div className="md:col-span-2 space-y-1.5">
                                {[5, 4, 3, 2, 1].map((n) => (
                                    <div key={n} className="flex items-center gap-2 text-sm">
                                        <span className="w-3 text-muted-foreground">{n}</span>
                                        <Star className="h-3 w-3 fill-amber-400 text-amber-400 flex-shrink-0" />
                                        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                            <div
                                                className="h-full bg-amber-400 rounded-full transition-all"
                                                style={{ width: `${((dist[n] || 0) / maxCount) * 100}%` }}
                                            />
                                        </div>
                                        <span className="text-xs text-muted-foreground w-10 text-right">{dist[n] || 0}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6 pt-6 border-t border-border flex flex-col sm:flex-row gap-3 items-center justify-between">
                            <p className="text-sm text-muted-foreground">Vous avez utilisé nos services récemment ?</p>
                            <Button
                                onClick={() => setShowForm(true)}
                                className="rounded-full gap-2"
                                data-testid="leave-review-btn"
                            >
                                <MessageSquare className="h-4 w-4" />
                                Laisser un avis
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Reviews list */}
                {loading ? (
                    <div className="flex justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : data.reviews.length === 0 ? (
                    <Card className="glass rounded-2xl">
                        <CardContent className="text-center py-10 text-muted-foreground">
                            <Quote className="h-10 w-10 mx-auto mb-3 opacity-40" />
                            Aucun avis pour le moment. Soyez le premier à laisser le vôtre !
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                        {data.reviews.map((r) => (
                            <Card key={r.review_id} className="glass rounded-2xl" data-testid={`review-${r.review_id}`}>
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between mb-2">
                                        <StarRating rating={r.rating} readonly size="sm" />
                                        {r.verified && (
                                            <Badge className="bg-emerald-500/10 text-emerald-600 border-0 text-xs">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                Client vérifié
                                            </Badge>
                                        )}
                                    </div>
                                    <h3 className="font-semibold mb-2">{r.title}</h3>
                                    <p className="text-sm text-muted-foreground mb-3 whitespace-pre-wrap">{r.comment}</p>
                                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
                                        <div className="flex items-center gap-2">
                                            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                                                {(r.author_name || 'C')[0].toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="font-medium text-foreground text-sm">{r.author_name}</p>
                                                {r.city && (
                                                    <p className="flex items-center gap-1 text-[11px]">
                                                        <MapPin className="h-2.5 w-2.5" />
                                                        {r.city}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <span>{formatRelativeDate(r.created_at)}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* CTA */}
                <Card className="glass rounded-3xl">
                    <CardContent className="p-6 text-center">
                        <h3 className="font-heading text-xl font-semibold mb-2">Besoin d&apos;aide pour votre piscine ?</h3>
                        <p className="text-muted-foreground mb-4">
                            Analyse d&apos;eau, conseils experts, boutique en ligne. Toujours à votre service.
                        </p>
                        <div className="flex gap-3 justify-center flex-wrap">
                            <Link to="/analysis">
                                <Button className="rounded-full">Faire mon analyse</Button>
                            </Link>
                            <Link to="/shop">
                                <Button variant="outline" className="rounded-full">Visiter la boutique</Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Review form dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Donnez votre avis sur O.C.A Piscine</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="text-center">
                            <Label>Votre note</Label>
                            <div className="flex justify-center mt-2">
                                <StarRating rating={form.rating} onChange={(v) => setForm({ ...form, rating: v })} size="lg" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="review-title">Titre <span className="text-destructive">*</span></Label>
                            <Input
                                id="review-title"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="Résumez votre expérience"
                                className="rounded-full mt-1"
                                maxLength={100}
                                data-testid="review-title-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="review-comment">Votre avis <span className="text-destructive">*</span></Label>
                            <Textarea
                                id="review-comment"
                                value={form.comment}
                                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                                placeholder="Partagez votre expérience avec O.C.A Piscine..."
                                rows={5}
                                maxLength={1500}
                                className="rounded-xl mt-1"
                                data-testid="review-comment-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">{form.comment.length}/1500</p>
                        </div>
                        {!user && (
                            <div>
                                <Label htmlFor="review-name">Votre prénom <span className="text-destructive">*</span></Label>
                                <Input
                                    id="review-name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="Marie L."
                                    className="rounded-full mt-1"
                                    required
                                />
                            </div>
                        )}
                        <div>
                            <Label htmlFor="review-city">Ville (optionnel)</Label>
                            <Input
                                id="review-city"
                                value={form.city}
                                onChange={(e) => setForm({ ...form, city: e.target.value })}
                                placeholder="Flayosc"
                                className="rounded-full mt-1"
                            />
                        </div>
                        <Button type="submit" disabled={submitting} className="w-full rounded-full gap-2" data-testid="submit-review-btn">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            Publier mon avis
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
